import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Panel, PanelSection, WindowContent, toast } from '@/shared/ui';
import { TextField } from '@/shared/forms';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { venuaPlannerQueryKeys } from '../../venuaPlannerApi';
import { SerieFormFields } from '../../serie';
import { ResponsiblePersonsPanel } from '../../components/ResponsiblePersonsPanel';
import { FORM_LABEL_WIDTH } from '../../formLayout';
import { productFormAgenda } from './productFormAgenda';
import { useProductForm } from './useProductForm';

interface ProductFormPageProps {
  layerId: string;
}

/**
 * Formulář pro založení nového produktu. Nad sdílenými poli série přidává Název produktu;
 * při uložení vznikne produkt (legacy + Phoenix) i jeho první série.
 */
export function ProductFormPage({ layerId }: ProductFormPageProps) {
  const { t } = useTranslation('venua-planner');
  const navigator = useNavigator();
  const queryClient = useQueryClient();
  const { open } = useAgendaParams(productFormAgenda, layerId);

  const closeForm = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  const { form } = useProductForm({
    projectId: open.projectId,
    onSaved: () => {
      void queryClient.invalidateQueries({ queryKey: venuaPlannerQueryKeys.tree() });
      toast({ severity: 'success', description: t('product.toast.saved') });
      closeForm();
    },
    onError: () => {
      toast({
        severity: 'error',
        title: t('product.toast.errorTitle'),
        description: t('product.toast.saveError'),
      });
    },
  });

  return (
    <WindowContent
      as="form"
      width="reading"
      data-testid="product-form"
      onSubmit={(event) => {
        event.preventDefault();
        void form.handleSubmit();
      }}
      footer={
        <form.Subscribe
          selector={(state) => ({
            canSubmit: state.canSubmit,
            isSubmitting: state.isSubmitting,
          })}
        >
          {({ canSubmit, isSubmitting }) => (
            <footer className="bg-phx-surface-2 border-phx-border-2 px-phx-5 gap-phx-2 py-phx-4 flex shrink-0 items-center justify-end border-t">
              <Button variant="ghost" size="sm" type="button" onClick={closeForm}>
                {t('product.actions.cancel')}
              </Button>
              <Button
                variant="primary"
                size="lg"
                type="submit"
                loading={isSubmitting}
                disabled={!canSubmit || isSubmitting}
                data-testid="product-save-button"
              >
                {t('product.actions.save')}
              </Button>
            </footer>
          )}
        </form.Subscribe>
      }
    >
      <Panel variant="filled" title={t('product.titleCreate')}>
        <PanelSection>
          <form.Field name="productName">
            {(field) => (
              <TextField
                field={field}
                label={t('product.name.label')}
                required
                layout="inline"
                labelWidth={FORM_LABEL_WIDTH}
                placeholder={t('product.name.placeholder')}
                data-testid="product-name-input"
              />
            )}
          </form.Field>
        </PanelSection>

        <SerieFormFields form={form} />
      </Panel>

      <ResponsiblePersonsPanel
        form={form}
        title={t('serie.persons.sectionLabel')}
        help={t('serie.persons.help')}
        placeholder={t('serie.persons.placeholder')}
        testIdPrefix="serie"
      />
    </WindowContent>
  );
}
